<script setup lang="ts">

import {toLocaleDateTime} from "@/lib/time";
import CreateTenantInstitutionCohort from "@/components/CreateCards/CreateTenantInstitutionCohortCard.vue";
import PaginationControls from "@/components/PaginationControls.vue";
import SearchTextField from "@/components/SearchTextField.vue";
import {onMounted, Ref, ref, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useI18n} from "vue-i18n";
import {Action, Entity, isYapiError, Paginated, Role, Tenant, TenantPaginationParams, YapiError} from "@YenzaCT/sdk";

import yapi from "@/lib/yapi";
import {useGlobalStore} from "@/store";

import {ColumnsConfig, FooterData, footerDataFactory, usePagination} from "@/lib/pagination";
import TableFilterCardItem from "@/components/TableFilterCardItem.vue";
import TableFilterCard from "@/components/TableFilterCard.vue";

const router = useRouter();
const route = useRoute();
const store = useGlobalStore();
const {t} = useI18n();

const tenants: Ref<Tenant[]> = ref([]);
const loadingData = ref(false);
const paginatedData: Ref<Paginated<Tenant> | undefined> = ref();
let paginationFooterData: FooterData = footerDataFactory();
const isAdminOrRoot = ref(store.user?.app.role === Role.Admin || store.user?.app.role === Role.Root);

const showCreateDialog = ref(false);
const searchString = ref(route.query.search as string);

const cols: ColumnsConfig = {
  id: {
    title: t("components.TenantTablePage.table_headers.id"),
    modelKey: "_id",
    visible: false,
    filter: ""
  },
  title: {
    title: t("components.TenantTablePage.table_headers.title"),
    modelKey: "title",
    visible: true,
    filter: ""
  },
  disabled: {
    title: t("components.TenantTablePage.table_headers.disabled"),
    modelKey: "disabled",
    visible: true,
    filter: ""
  },
  createdAt: {
    title: t("components.TenantTablePage.table_headers.created_at"),
    modelKey: "createdAt",
    visible: true,
    filter: ""
  }
};

const {
  columns,
  headers,
  page,
  perPage,
  sort,
  onUpdatePerPage,
  onSort,
  onNext,
  onPrev,
  onPageChange,
  setupFooterData,
  activeFilterCount,
  resetFilter
} = usePagination(fetchPage, {
  initialColumns: cols,
  initialSort: [{
    key: "createdAt",
    order: "desc"
  }]
});

async function fetchPage() {
  loadingData.value = true;

  const params: TenantPaginationParams = {
    page: page.value,
    size: perPage.value,
    sortBy: sort.value[0].key,
    sortOrder: sort.value[0].order,
  };
  params.title = columns.title.filter as string || undefined;
  try {
    store.networkBusy = true;
    paginatedData.value = (await yapi.admin.tenant.paginate(params)).data;
    tenants.value = paginatedData.value.docs;
    paginationFooterData = setupFooterData(paginatedData.value);
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  } finally {
    store.networkBusy = false;
    loadingData.value = false;
  }
}

onMounted(async () => {
  loadingData.value = true;

  if (searchString.value) {
    handleSearch(searchString.value);
    return;
  }
  await fetchPage();
  loadingData.value = false;
});

/**
 * Watch for changes to the search query param when using global search
 */
watch(() => route.query.search, async (newSearchValue) => {
  if (newSearchValue && newSearchValue !== searchString.value) {
    resetFilter();
    handleSearch(newSearchValue as string);
  }
});

const onClickRow = async (event: Event, row: { internalItem: { raw?: Tenant } }) => {
  try {
    if (row.internalItem && row.internalItem.raw) {
      await openTenantDetails(row.internalItem.raw._id as string);
    }
  } catch (e) {
    if (isYapiError(e)) {
      const yError = e as YapiError;
      await store.handleYapiError(yError);
    } else {
      throw e;
    }
  }
};

function handleReset() {
  searchString.value = "";
  resetFilter();
}

function handleSearch(searchText: string) {
  columns.title.filter = searchText;
}

const openTenantDetails = async (id: string) => {
  await router.push({
    name: "tenantDetail",
    params: {id}
  });
};

</script>

<template>
  <v-row>
    <v-col cols="12">
      <v-table-toolbar>
        <v-spacer/>
        <v-btn-primary
          v-if="store.hasAbility(Entity.Tenant, Action.create) || isAdminOrRoot"
          prepend-icon="far fa-plus"
          data-pw="createTenantButton"
          @click="showCreateDialog = true"
        >
          {{ $t("buttons.create") }}
        </v-btn-primary>
      </v-table-toolbar>
    </v-col>
  </v-row>

  <v-row>
    <v-col>
      <table-filter-card  @resetFilters="handleReset" :active-filter-count="activeFilterCount">
        <table-filter-card-item title="Search">
          <search-text-field
            :label="$t('common.search_title')"
            :disabled="store.networkBusy"
            :search-string="searchString"
            @update:text="text => {
              handleSearch(text)
            }"
          />
        </table-filter-card-item>
      </table-filter-card>
    </v-col>
    <v-col md="9" lg="10">
      <v-data-table-server
        class="mb-6"
        density="compact"
        :loading="loadingData"
        :headers="headers"
        :items="tenants"
        :items-per-page="perPage"
        :items-length="0"
        data-pw="tenantsDataTable"
        @click:row="onClickRow"
        @update:sort-by="onSort"
        :sort-by="sort"
        :hover="true"
        hide-default-footer
      >

        <template v-slot:no-data>
          <h5 class="pa-2 d-flex align-center justify-center align-items-center">
            {{ $t("tenant.no_data_message") }}
          </h5>
        </template>

        <template v-slot:loader>
          <v-sheet
            class="d-flex align-center justify-center"
            height="100%"
          >
            <v-progress-linear
              indeterminate
              color="primary"
            ></v-progress-linear>
          </v-sheet>
        </template>

        <template v-slot:item.disabled="{ item }">
          <v-icon
            v-if="!item.disabled"
            size="x-small"
          >
            far fa-close
          </v-icon>
          <v-icon v-else>
            far fa-check
          </v-icon>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-icon @click="openTenantDetails(item._id as string)"> far fa-pen-to-square</v-icon>
        </template>

        <template v-slot:item.createdAt="{ item }">
          {{ toLocaleDateTime(item.createdAt) }}
        </template>

        <template v-slot:bottom>
          <v-divider/>
          <pagination-controls
            v-if="paginatedData"
            class="text-center pt-2 v-table__pagination-controls"
            :pagination-footer-data="paginationFooterData"
            @update:per-page="onUpdatePerPage"
            @on-next="onNext"
            @on-prev="onPrev"
            @on-page-change="onPageChange"
            data-pw="tenantPaginationControls"
          />
        </template>
      </v-data-table-server>
    </v-col>
  </v-row>

  <v-dialog v-model="showCreateDialog" max-width="500">
    <create-tenant-institution-cohort
      :entity-type="Entity.Tenant"
      :entity-title="$t('components.CreateCards.CreateTenantInstitutionCohort.titleCreateTenant')"
      :allowed="store.hasAbility(Entity.Tenant, Action.create) || isAdminOrRoot"
      data-pw="createTenantCard"
      @close="showCreateDialog = false"
    />
  </v-dialog>
</template>

<style scoped>

</style>
